import { useSession, useDataset, LogoutButton } from "@inrupt/solid-ui-react";
import { getSolidDataset, getThing, getThingAll, getUrlAll } from "@inrupt/solid-client";
import { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  FormGroup,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
} from "@mui/material";

import { getOrCreateCommonplaceBook } from "../lib/util";
import { STORAGE_PREDICATE } from "../lib/predicates";

import Login from "./solid/Login";
import AddQuote from "./solid/AddQuote";
import QuoteList from "./solid/QuoteList";
import PublicAddQuote from "./solid/PublicAddQuote";
import PublicQuoteList from "./solid/PublicQuoteList";
import Header from "./Header";

const SOLID_POD_URI =
  "https://storage.inrupt.com/0ceecbd1-59d2-4858-8e73-a819d5bdb943/commonplace-book/index.ttl";

//TODO: Check for existing commonplace container on login, but ASK before creating a folder
//TODO: can webid be verified without giving storage access for contribution only?
function KnowledgeLists({
  handleShowLanding,
  handleShowCommonplace,
  listsToDisplay,
  handleListsToDisplay,
  handleShowKnowledgeLists,
  currentView = "knowledgeLists",
}) {
  const { session } = useSession();
  const { dataset, error: communalError } = useDataset(SOLID_POD_URI);
  const [personalQuoteList, setPersonalQuoteList] = useState();
  const [showPersonal, setShowPersonal] = useState(false);
  const [showPublic, setShowPublic] = useState(true);
  const [publicQuoteList, setPublicQuoteList] = useState(dataset);
  const [personalLoadError, setPersonalLoadError] = useState(null);
  const [personalLoading, setPersonalLoading] = useState(false);

  const getSessionPodString = async () => {
    const profileDataset = await getSolidDataset(session.info.webId, {
      fetch: session.fetch,
    });
    const profileThing = getThing(profileDataset, session.info.webId);
    const podsUrls = getUrlAll(profileThing, STORAGE_PREDICATE);
    const pod = podsUrls[0];
    return pod;
  };

  // Keep public list state in sync with communal dataset (so 3D scene gets it)
  useEffect(() => {
    if (dataset) setPublicQuoteList(dataset);
  }, [dataset]);

  // Load visitor's personal quote list on pod connection
  useEffect(() => {
    if (!session?.info?.isLoggedIn) return;

    setPersonalLoadError(null);
    setPersonalLoading(true);

    (async () => {
      try {
        const pod = await getSessionPodString();
        if (!pod) {
          setPersonalLoadError("No storage location found in your profile.");
          return;
        }
        const containerUri = `${pod}commonplace-book/`;
        const quotes = await getOrCreateCommonplaceBook(
          containerUri,
          session.fetch
        );
        setPersonalQuoteList(quotes);
        setShowPersonal(true);
      } catch (err) {
        console.error("Personal pod load error:", err);
        setPersonalLoadError(
          err?.message || "We couldn’t load your pod yet. Check your connection and profile."
        );
      } finally {
        setPersonalLoading(false);
      }
    })();
  }, [session, session?.info?.isLoggedIn]);

  useEffect(() => {
    //send list to parent component
    let quoteObjectArray = [];
    if (showPersonal) quoteObjectArray.push(personalQuoteList);
    if (showPublic) quoteObjectArray.push(publicQuoteList);
    handleListsToDisplay(quoteObjectArray);
  }, [showPersonal, showPublic, personalQuoteList, publicQuoteList]);

  const handlePublicQuoteList = (updatedDataset) => {
    setPublicQuoteList(updatedDataset);
  };

  return (
    <>
      <Header
        handleShowLanding={handleShowLanding}
        handleShowCommonplace={handleShowCommonplace}
        handleShowKnowledgeLists={handleShowKnowledgeLists}
        currentView={currentView}
      />
      <Container className="solid-main" sx={{ margin: (t) => t.spacing(2) }}>
        <Box sx={{ mb: (t) => t.spacing(2) }}>
          <Typography variant="h3">Personalize and Contribute</Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your quotes and choose what appears in the Road and Sky views.
          </Typography>
        </Box>
        {session.info.isLoggedIn ? (
          <Box className="connected" sx={{ padding: (t) => t.spacing(1) }}>
            <Typography variant="h5">
              You are connected via webId: {session.info.webId}
            </Typography>
            <Box className="logout-container">
              <LogoutButton />
            </Box>

            <Paper className="personal-quotes" sx={{ margin: (t) => t.spacing(1), padding: (t) => t.spacing(2) }}>
              <Typography variant="h3">Personalize</Typography>
              {personalLoading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading your pod…</Typography>
                </Box>
              )}
              {personalLoadError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setPersonalLoadError(null)}>
                  {personalLoadError}
                </Alert>
              )}
              <Box className="add-quote">
                <AddQuote
                  quoteList={personalQuoteList}
                  setQuoteList={setPersonalQuoteList}
                />
                {personalQuoteList && getThingAll(personalQuoteList).length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Your list is empty. Add a quote above to see it in the Road or Sky view.
                  </Typography>
                )}
              </Box>

              <Box className="show-list-checkbox" sx={{ mt: (t) => t.spacing(2) }}>
                <FormGroup sx={{ padding: (t) => t.spacing(0.5) }}>
                  <FormControlLabel
                    control={<Checkbox defaultChecked />}
                    label="Show List in Three Scene?"
                    name="myCheckbox"
                    id="myCheckbox"
                    checked={showPersonal}
                    onChange={(e) => setShowPersonal(!showPersonal)}
                  />
                </FormGroup>
              </Box>
              <Box className="personal-quote-list">
                <QuoteList
                  quoteList={personalQuoteList}
                  setQuoteList={setPersonalQuoteList}
                  listOwner={session.info.webId}
                />
              </Box>
            </Paper>
            <Paper className="app-quotes" sx={{ margin: (t) => t.spacing(1), padding: (t) => t.spacing(2) }}>
              <Typography variant="h3">
                Contribute to the Public Quote List
              </Typography>
              {!dataset && !communalError && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading public list…</Typography>
                </Box>
              )}
              {communalError && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  We couldn’t load the communal quote list yet. You may not be able to contribute until it loads.
                </Alert>
              )}
              <Box className="add-quote">
                <PublicAddQuote
                  publicQuoteList={publicQuoteList}
                  setPublicQuoteList={setPublicQuoteList}
                  handlePublicQuoteList={handlePublicQuoteList}
                />
                {dataset && getThingAll(dataset).length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    The public list is empty. Add a quote above to be the first to contribute.
                  </Typography>
                )}
              </Box>
              <Box className="show-list-checkbox" sx={{ mt: (t) => t.spacing(2) }}>
                <FormGroup sx={{ padding: (t) => t.spacing(0.5) }}>
                  <FormControlLabel
                    control={<Checkbox defaultChecked />}
                    label="Show List in Three Scene?"
                    name="myCheckbox"
                    id="myCheckbox"
                    checked={showPublic}
                    onChange={(e) => setShowPublic(!showPublic)}
                  />
                </FormGroup>
              </Box>
              <Box className="public-quote-list">
                <PublicQuoteList
                  publicQuoteList={dataset}
                  setPublicQuoteList={setPublicQuoteList}
                  handlePublicQuoteList={handlePublicQuoteList}
                />
              </Box>
            </Paper>
          </Box>
        ) : (
          <Box className="connect-pod">
            <Typography
              variant="h5"
              component="div"
              sx={{ padding: (t) => t.spacing(2) }}
            >
              Connect your pod to add and manage your own quotes
            </Typography>
            <Typography variant="body1" component="div" sx={{ padding: (t) => t.spacing(1) }}>
              Connect your Solid POD (personal online datastore) to personalize your CommonSpace experience.
            </Typography>
            <Login />

            <Typography variant="body1" component="div" sx={{ padding: (t) => t.spacing(1) }}>
              Don’t have a POD yet? You can get one from a provider or host your own.
            </Typography>

            <Box className="button-list" sx={{ "& > *": { m: (t) => t.spacing(1) } }}>
              <Button
                href="https://solidproject.org/users/get-a-pod#get-a-pod-from-a-pod-provider"
                target="_blank"
                variant="contained"
              >
                Cloud Pod Providers
              </Button>
              <Button
                href="https://solidproject.org//developers/tutorials/getting-started#own-server"
                target="_blank"
                variant="contained"
              >
                Host Local Pod
              </Button>
              <Button
                href="https://www.youtube.com/playlist?list=PLYQDrJ8RYKOGSyhHh4kr-EbJx0njxz_8P"
                target="_blank"
                variant="contained"
              >
                Video Walkthroughs
              </Button>
              <Button
                href="https://solidproject.org/about"
                target="_blank"
                variant="contained"
              >
                About Solid
              </Button>
            </Box>
          </Box>
        )}
        <Box className="nav-buttons" sx={{ margin: (t) => t.spacing(2), display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={handleShowLanding}>
            Return Home
          </Button>
          <Button variant="contained" onClick={handleShowCommonplace}>
            Enter CommonSpace
          </Button>
        </Box>

        <div></div>
      </Container>
    </>
  );
}
export default KnowledgeLists;
